import React from 'react';
import { Card, CardContent } from "../components/ui/card.tsx";
import { Button } from "../components/ui/button.tsx";
import { TrendingUp, TrendingDown, Users, BookOpen, Clock } from "lucide-react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const performanceData = [
  { month: "Jan 2022", average: 20 },
  { month: "Feb 2022", average: 25 },
  { month: "Mar 2022", average: 35 },
  { month: "Apr 2022", average: 45 },
  { month: "May 2022", average: 55 },
  { month: "Jun 2022", average: 62 },
  { month: "Jul 2022", average: 58 },
  { month: "Aug 2022", average: 48 },
  { month: "Sep 2022", average: 52 },
  { month: "Oct 2022", average: 67 },
];

const metrics = [
  {
    title: "Total Students",
    value: "3,671",
    change: "+2.5%",
    trend: "up",
    icon: Users,
    color: "bg-purple-100/80 text-purple-700",
  },
  {
    title: "Average Performance",
    value: "85%",
    change: "+4.3%",
    trend: "up",
    icon: TrendingUp,
    color: "bg-blue-100/80 text-blue-700",
  },
  {
    title: "Attendance Rate",
    value: "92%",
    change: "-0.5%",
    trend: "down",
    icon: Clock,
    color: "bg-green-100/80 text-green-700",
  },
  {
    title: "Course Completion",
    value: "78%",
    change: "+1.2%",
    trend: "up",
    icon: BookOpen,
    color: "bg-orange-100/80 text-orange-700",
  },
];

const TimeFilter = ({ title }) => (
  <div className="flex items-center gap-2 mb-4">
    <h3 className="text-sm font-medium flex-1">{title}</h3>
    <div className="flex items-center gap-1 text-xs">
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs hover:bg-gray-100">D</Button>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs hover:bg-gray-100">W</Button>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs bg-purple-100 text-purple-700">M</Button>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs hover:bg-gray-100">Y</Button>
      <Button variant="ghost" size="sm" className="h-7 px-3 text-xs hover:bg-gray-100">Custom Time</Button>
    </div>
  </div>
);

export default function Analytics() {
  return (
    <div className="space-y-6 p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl ${metric.color}`}>
                  <metric.icon className="h-5 w-5" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-medium ${
                    metric.trend === "up" ? "text-green-600" : "text-red-600"
                  }`}>
                    {metric.change}
                  </span>
                  {metric.trend === "up" ? (
                    <TrendingUp className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5 text-red-600" />
                  )}
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-1">{metric.title}</p>
                <p className="text-2xl font-semibold tracking-tight">{metric.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Academic Performance Chart */}
        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <TimeFilter title="Academic Performance" />
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={performanceData} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#86EFAC" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#86EFAC" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94A3B8", fontSize: 11 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94A3B8", fontSize: 11 }}
                    dx={-10}
                    domain={[0, 100]}
                    ticks={[0, 20, 40, 60, 80, 100]}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="rounded-lg border bg-white p-2 shadow-sm">
                            <p className="text-xs font-medium mb-1">{label}</p>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-600">Avg Marks:</span>
                              <span className="font-medium">{payload[0].value}</span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="average"
                    stroke="#22C55E"
                    fill="url(#performanceGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-between items-center mt-2 text-[10px] text-gray-500">
              <span>X = Time Period</span>
              <span>Y = Average Marks</span>
            </div>
          </CardContent>
        </Card>

        {/* Leads Chart */}
        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <TimeFilter title="Leads" />
            <div className="flex gap-8 justify-center items-center h-[280px]">
              <div className="relative w-[200px] h-[200px]">
                <div className="w-full h-full rounded-full border-[16px] border-emerald-500" style={{ clipPath: 'inset(0 0 50% 0)' }} />
                <div className="absolute top-0 left-0 w-full h-full rounded-full border-[16px] border-yellow-400" style={{ clipPath: 'inset(50% 0 0 50%)' }} />
                <div className="absolute top-0 left-0 w-full h-full rounded-full border-[16px] border-gray-700" style={{ clipPath: 'inset(50% 50% 0 0)' }} />
                <div className="absolute top-0 left-0 w-full h-full rounded-full border-[16px] border-red-500" style={{ clipPath: 'inset(0 50% 0 0)' }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-[120px] h-[120px] rounded-full bg-white"></div>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  <span className="text-gray-600">Converted (56%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                  <span className="text-gray-600">Assigned (31%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-gray-700" />
                  <span className="text-gray-600">Unassigned (10%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                  <span className="text-gray-600">Dropped (15%)</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 